import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Users as UsersIcon, Plus, AlertTriangle, Tv } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient, extractErrorMessage } from '@/lib/api';
import UserFormModal from '@/components/users/UserFormModal';
import { formatRelative } from '@/lib/format';

interface User {
  id: string;
  code: string;
  role: string;
  fullName: string;
  phone: string;
  email: string | null;
  status: string;
  lastLoginAt: string | null;
  createdAt: string;
  businessUnitCodes: string[];
}

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin', admin: 'Admin', accountant: 'Accountant',
  fleet_officer: 'Operations Manager', site_manager: 'Site Manager',
  construction_supervisor: 'Site Supervisor', driver: 'Driver',
  investor: 'Investor', auditor: 'Viewer',
  tv_display: 'TV Wall Display',
};

const STATUS_TONE: Record<string, string> = {
  active:    'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  inactive:  'bg-stone-500/15 text-stone-700 dark:text-stone-400',
  suspended: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  locked:    'bg-rose-500/15 text-rose-700 dark:text-rose-400',
};

function relTime(iso: string | null): string {
  return iso ? formatRelative(iso) : 'never';
}

export default function UsersPage() {
  const navigate = useNavigate();
  const [list, setList] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [unitFilter, setUnitFilter] = useState<string>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [createdResult, setCreatedResult] = useState<{ user: { id: string; code: string; role: string; fullNameEn: string }; password: string } | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await apiClient.get<User[]>('/api/v1/users');
      setList(r.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const allBuCodes = useMemo(() => {
    const s = new Set<string>();
    for (const u of list) for (const c of u.businessUnitCodes) s.add(c);
    return Array.from(s).sort();
  }, [list]);

  const filtered = useMemo(() => list.filter(u => {
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    if (statusFilter !== 'all' && u.status !== statusFilter) return false;
    if (unitFilter !== 'all' && !u.businessUnitCodes.includes(unitFilter)) return false;
    if (search) {
      const q = search.toLowerCase();
      const blob = (u.code + ' ' + u.fullName + ' ' + u.phone + ' ' + (u.email ?? '')).toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  }), [list, search, roleFilter, statusFilter, unitFilter]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><UsersIcon className="h-4 w-4" /> Users &amp; permissions</div>
          <div className="flex items-center gap-2">
            <Link to="/dashboard/settings/tv-signins"><Button variant="outline" size="sm" className="gap-2"><Tv className="h-4 w-4" /> TV sign-ins</Button></Link>
            <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" /> Create user
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <Input placeholder="Search code, name, phone, email…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
            className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
            <option value="all">All roles</option>
            {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
            <option value="locked">Locked</option>
          </select>
          <select value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}
            className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
            <option value="all">All business units</option>
            {allBuCodes.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs text-muted-foreground bg-muted/40 border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-2">Code</th>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Role</th>
                    <th className="text-left px-3 py-2">Business units</th>
                    <th className="text-left px-3 py-2">Phone</th>
                    <th className="text-left px-3 py-2">Last login</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground">No users match these filters.</td></tr>
                  ) : filtered.map(u => (
                    <tr key={u.id}
                      onClick={() => navigate('/dashboard/settings/users/' + u.id)}
                      className="border-b border-border last:border-b-0 hover:bg-muted/20 cursor-pointer">
                      <td className="px-3 py-2 font-mono text-xs">{u.code}</td>
                      <td className="px-3 py-2">{u.fullName}</td>
                      <td className="px-3 py-2 text-xs">{ROLE_LABELS[u.role] ?? u.role}</td>
                      <td className="px-3 py-2 text-xs">
                        {u.businessUnitCodes.length === 0 ? '—' : (
                          <div className="flex gap-1 flex-wrap">
                            {u.businessUnitCodes.map(c => (
                              <span key={c} className="inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono">{c}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono">{u.phone}</td>
                      <td className="px-3 py-2 text-xs">{relTime(u.lastLoginAt)}</td>
                      <td className="px-3 py-2">
                        <span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ' + (STATUS_TONE[u.status] ?? '')}>
                          {u.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="text-xs text-muted-foreground">{filtered.length} of {list.length} users shown</div>
      </main>

      {createOpen && (
        <UserFormModal
          mode="create"
          onClose={() => setCreateOpen(false)}
          onCreated={(user, password) => {
            setCreateOpen(false);
            setCreatedResult({ user, password });
            refresh();
          }}
        />
      )}

      {createdResult && (
        <CreatedUserModal
          user={createdResult.user}
          password={createdResult.password}
          onClose={() => setCreatedResult(null)}
        />
      )}
    </div>
  );
}

interface CreatedUser { id: string; code: string; role: string; fullName?: string; fullNameEn?: string }
function CreatedUserModal({ user, password, onClose }: { user: CreatedUser; password: string; onClose: () => void }) {
  const displayName = user.fullName ?? user.fullNameEn ?? user.code;
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  async function downloadPdf() {
    const r = await apiClient.post('/api/v1/users/' + user.id + '/credentials-sheet.pdf', { password }, { responseType: 'blob' });
    const url = window.URL.createObjectURL(r.data as Blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'credentials_' + user.code + '.pdf';
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card border border-border rounded-lg shadow-2xl max-w-lg w-full p-6 space-y-4">
        <div>
          <h3 className="text-lg font-medium">User created</h3>
          <p className="text-sm text-muted-foreground mt-1">{displayName} ({user.code}) — {ROLE_LABELS[user.role] ?? user.role}</p>
        </div>
        <div className="rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-800 dark:text-amber-400 p-3 text-sm">
          <strong>This password will not be shown again.</strong> Save it now or download the credentials sheet.
        </div>
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground">Temporary password</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-base bg-muted rounded px-3 py-2 select-all break-all">{password}</code>
            <Button size="sm" variant="outline" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <Button onClick={downloadPdf} className="gap-2">Download credentials sheet (PDF)</Button>
          <Button variant="ghost" onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  );
}
